import React, {
  useLayoutEffect, useRef, useContext
} from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Dropdown as StencilDropDown, MenuItem } from '@one-thd/sui-atomic-components';
import classnames from 'classnames';
import proptypes from 'prop-types';
import { AttributeLabel } from '../attribute-label/attribute-label';
import { DropdownItem } from '../dropdown/dropdown-item';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';

const {
  string, func, bool, node, number, shape
} = proptypes;

const renderDropdownItems = (children) => {
  const attributeValues = React.Children
    .toArray(children?.props?.children ?? children)
    .filter(Boolean);
  return attributeValues
    .filter((child) => React.isValidElement(child) && child.props?.value != null)
    .map((child) => {
      const { value, swatchGuid, isAvailable, onClick } = child.props;
      return (
        <MenuItem
          key={value}
          value={value}
          onClick={onClick}
        >
          <DropdownItem
            swatchGuid={swatchGuid}
            attributeValue={value}
            isDisabled={!isAvailable}
          />
        </MenuItem>
      );
    });
};

export const SuperSkuAttribute = (props) => {
  const {
    attributeName,
    attributeValue,
    attributeType,
    brandName,
    children,
    center,
    handleAttributeOverflowReset,
    handleAttributeOverflowUpdate,
    isOverflowSection,
    info,
    paintType,
    isPaintReorder,
    scrollHeight,
    miniSwatch,
    sizeGuideInfo,
    swatchGuid,
    large,
    hexColor,
    itemId,
    containerSizeHref,
    isOOS,
    isMobile,
    attributeIndex
  } = props;

  const { channel } = useContext(ExperienceContext);
  const isDropdown = attributeType === 'dropdown';
  const isSwatch = attributeType === 'swatch';
  const isCard = attributeType === 'card';
  const isTile = attributeType === 'tile';

  const heights = {
    card: 'none',
    swatch: '165px',
    tile: '102px',
  };

  const wrapperMaxHeight = !isDropdown && !isCard && (heights[attributeType] || '96px');
  const isMobileLargeCard = isMobile && isCard && large;

  const attributeWrapperClasses = classnames({
    'sui-flex sui-flex-row sui-justify-start': !isDropdown && !isCard,
    'sui-block': isCard && large,
    'sui-flex-wrap sui-overflow-hidden': !isDropdown && !isCard,
    'sui-flex sui-flex-row sui-overflow-x-auto sui-scrollbar-hide': isMobileLargeCard,
    'sui-my-1': isCard,
    'sui-max-h-none': isCard,
    'sui-flex-nowrap': (isCard && !large) || isMobileLargeCard,
    'sui-gap-2': isSwatch || isTile,
    'sui-gap-3': isMobileLargeCard,
  });

  const attributeContainerClasses = classnames('sui-flex sui-flex-col', {
    'sui-justify-center': center
  });

  const attributeRef = useRef(null);

  const { dimensions } = useWindowDimensions();

  useLayoutEffect(() => {
    if (handleAttributeOverflowReset) {
      handleAttributeOverflowReset(attributeRef?.current?.scrollHeight, attributeName);
    }
  }, [dimensions, itemId, attributeName]);

  useLayoutEffect(() => {
    if (handleAttributeOverflowUpdate && !isDropdown) {
      handleAttributeOverflowUpdate(attributeRef, attributeName);
    }
  }, [scrollHeight, isOverflowSection, isDropdown, attributeName]);

  return (
    !miniSwatch ? (
      <div className="sui-flex sui-flex-col sui-mb-4" data-component="SuperSkuAttribute">
        <AttributeLabel
          attributeName={attributeName}
          attributeLabelValue={attributeValue}
          brandName={brandName}
          sizeGuideInfo={sizeGuideInfo}
          hexColor={hexColor}
          info={info}
          paintType={paintType}
          isPaintReorder={isPaintReorder}
          containerSizeHref={containerSizeHref}
          isOOS={isOOS}
          isMobile={isMobile}
          attributeIndex={attributeIndex}
        />
        <div
          className={attributeWrapperClasses}
          style={{ maxHeight: wrapperMaxHeight }}
          ref={attributeRef}
        >
          {isDropdown ? (
            <StencilDropDown
              value={attributeValue}
              disableMenuCloseOnScroll
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 600,
                    zIndex: 9999,
                  }
                },
                MenuListProps: {
                  style: {
                    padding: 0,
                    overflowY: 'auto',
                  }
                }
              }}
              placeholder={<DropdownItem swatchGuid={swatchGuid} attributeValue={attributeValue} />}
            >
              {renderDropdownItems(children)}
            </StencilDropDown>
          ) : children}
        </div>
      </div>
    )
      : !isDropdown && !isCard && (
        <div
          className={attributeContainerClasses}
          data-component="SuperSkuAttributeMini"
        >
          <div className="sui-whitespace-nowrap">
            {children}
          </div>
        </div>
      )
  );
};

SuperSkuAttribute.propTypes = {
  attributeName: string.isRequired,
  attributeValue: string,
  attributeType: string.isRequired,
  brandName: string,
  children: node.isRequired,
  center: bool,
  handleAttributeOverflowReset: func,
  handleAttributeOverflowUpdate: func,
  hexColor: string,
  isOverflowSection: bool,
  info: shape({
    paintBrand: string,
  }),
  isPaintReorder: bool,
  large: bool,
  miniSwatch: bool,
  paintType: string,
  scrollHeight: number,
  sizeGuideInfo: shape({
    collectionId: string,
    isSize: bool,
    isTowelSize: bool,
    parentId: string,
    productType: string
  }),
  swatchGuid: string,
  itemId: string,
  containerSizeHref: string
};

SuperSkuAttribute.defaultProps = {
  attributeValue: null,
  brandName: '',
  center: false,
  handleAttributeOverflowReset: () => { },
  handleAttributeOverflowUpdate: () => { },
  hexColor: '',
  isOverflowSection: false,
  info: {
    paintBrand: '',
  },
  isPaintReorder: false,
  large: false,
  miniSwatch: false,
  paintType: '',
  scrollHeight: 0,
  sizeGuideInfo: {
    collectionId: '',
    isSize: false,
    isTowelSize: false,
    parentId: '',
    productType: ''
  },
  swatchGuid: '',
  itemId: '',
  containerSizeHref: ''
};
